import { Modal, Button, notification, Space, Tag } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import ShopifyProductImageDetailDescription from './ShopifyProductImageDetailDescription';
import { analyzeShopifyProductImage } from '../../api/analyzeShopifyProductImage';
import { statusApprovedActionColor, statusColorGeneration } from '../../constants/statusColors';
import { fetchShopifyMedia, refetchShopifyMedia, setSelectedShopifyMedia, updateShopifyMedia, updateShopifyMediaGeneration } from '../../store/shopifyMediaSlice';
import { GenerationStatus } from '../../constants/status';
import { CheckOutlined, CloseOutlined, LeftOutlined, RightOutlined, RobotOutlined, StopOutlined } from '@ant-design/icons';

interface ShopifyProductImageDetailModalProps {
    open: boolean;
    handleClose: () => void;
}

const ShopifyProductImageDetailModal: React.FC<ShopifyProductImageDetailModalProps> = ({ open, handleClose }) => {

    const dispatch = useAppDispatch()

    const selectedShopifyShop = useAppSelector((state) => state.shopifyShops.selectedShopifyShop)
    const selectedShopifyProduct = useAppSelector((state) => state.shopifyProducts.selectedShopifyProduct)
    const selectedShopifyMedia = useAppSelector((state) => state.shopifyMedia.selectedShopifyMedia)
    const shopifyMedia = useAppSelector((state) => state.shopifyMedia.shopifyMedia)
    const pagination = useAppSelector((state) => state.shopifyMedia.pagination)
    const activeFilters = useAppSelector((state) => state.shopifyMedia.activeFilters)
    const activeSorter = useAppSelector((state) => state.shopifyMedia.activeSorter)

    const [currentProductIndex, setCurrentProductIndex] = useState<number>(0)
    const [analyzing, setAnalyzing] = useState<boolean>(false)

    useEffect(() => {
        // TODO: Make this one work with the filtered Products as well
        if (open && selectedShopifyMedia) {
            const index = shopifyMedia.findIndex(obj => obj.id === selectedShopifyMedia.id);
            setCurrentProductIndex(index);
        }
    }, [open, shopifyMedia, selectedShopifyMedia]);

    const handleNext = () => {
        const newIndex = Math.min(currentProductIndex + 1, shopifyMedia.length)
        setCurrentProductIndex(newIndex)
        dispatch(setSelectedShopifyMedia(shopifyMedia[newIndex]))

        // Check if you can fetch more products
        if (selectedShopifyShop) {
            if (newIndex === shopifyMedia.length - 2 && pagination.lastVisibleDoc && pagination.hasMore) {
                dispatch(
                    fetchShopifyMedia({
                        shopifyShopId: selectedShopifyShop?.id,
                        pageSize: pagination.pageSize,
                        lastVisibleDoc: pagination.lastVisibleDoc,
                        activeSorter: activeSorter,
                        activeFilters: activeFilters,
                        selectedShopifyProductId: selectedShopifyProduct?.id,
                        refetch: false
                    })
                );
            }
        }
    }

    const handleApproveAndNext = (currentProductIndex: number) => {
        if (selectedShopifyMedia && selectedShopifyShop) {
            dispatch(updateShopifyMedia({ id: selectedShopifyMedia.id, shopId: selectedShopifyShop.id, generationStatus: GenerationStatus.APPROVED }));
        }
        if (currentProductIndex < shopifyMedia.length - 1) {
            const newIndex = currentProductIndex + 1
            setCurrentProductIndex(newIndex)
            dispatch(setSelectedShopifyMedia(shopifyMedia[newIndex]))
        }
    }

    const handleUnapprove = (currentProductIndex: number) => {
        if (selectedShopifyMedia && selectedShopifyShop) {
            dispatch(updateShopifyMedia({ id: selectedShopifyMedia.id, shopId: selectedShopifyShop.id, generationStatus: GenerationStatus.GENERATED }));
        }
    }

    const handleLast = () => {
        const newIndex = Math.max(currentProductIndex - 1, 0)
        setCurrentProductIndex(newIndex)
        dispatch(setSelectedShopifyMedia(shopifyMedia[newIndex]))
    }

    const handleGenerateShopifyProductImage = async () => {
        console.log("Starting generation for:", selectedShopifyMedia, selectedShopifyShop);

        // Check if required data is available
        if (!selectedShopifyMedia || !selectedShopifyShop) {
            console.error("Image or Shopify shop is not selected.");
            return;
        }

        try {
            setAnalyzing(true);
            console.log("Generating product image content...");

            await analyzeShopifyProductImage(selectedShopifyShop.id, selectedShopifyMedia.id);
            dispatch(refetchShopifyMedia({
                shopifyShopId: selectedShopifyShop.id,
                mediaId: selectedShopifyMedia.id
            }))

        } catch (error) {
            console.error("Error generating product:", error);

            notification.error({
                message: "Content Generation Failed",
                // description: error.message || "An error occurred during analysis.",
            });
        } finally {
            setAnalyzing(false);
        }
    };

    const handleDiscard = (type: "Alt") => {
        if (selectedShopifyMedia && selectedShopifyShop) {
            setAnalyzing(true)
            dispatch(updateShopifyMediaGeneration({
                id: selectedShopifyMedia.id,
                shopId: selectedShopifyShop.id,
                generation: {
                    alt: type === "Alt" ? selectedShopifyMedia.alt : selectedShopifyMedia.generation?.alt
                }
            }))
            setAnalyzing(false)
        }
    }

    const isBlocked = useMemo(() => {
        return selectedShopifyMedia?.generationStatus == GenerationStatus.BLOCKED
    }, [selectedShopifyMedia])


    return (
        <Modal
            open={open}
            title={
                <Space size="small">
                    {selectedShopifyMedia?.generationStatus && (
                        <Tag color={statusColorGeneration[selectedShopifyMedia.generationStatus]}>
                            {selectedShopifyMedia.generationStatus}
                        </Tag>
                    )}
                    <span>{'Shopify Product Image'}</span>
                </Space>
            }
            width={"95vw"}
            height={"85vh"}
            onCancel={handleClose}
            footer={[
                <Button
                    key="generate"
                    type="primary"
                    disabled={analyzing}
                    loading={analyzing}
                    icon={<RobotOutlined />}
                    onClick={handleGenerateShopifyProductImage}
                >
                    {analyzing ? "Generating" : "Generate Content"}
                </Button>,
                <Button key="last" type="default" icon={<LeftOutlined />} disabled={currentProductIndex === 0} onClick={handleLast}>
                    Last
                </Button>,
                <Button key="next" type="default" icon={<RightOutlined />} disabled={currentProductIndex === shopifyMedia.length - 1} onClick={handleNext}>
                    Next
                </Button>,
                <Button key="unapprove" type="default" icon={<StopOutlined />} disabled={selectedShopifyMedia?.generationStatus !== GenerationStatus.APPROVED} onClick={() => handleUnapprove(currentProductIndex)}>
                    Unapprove
                </Button>,
                <Button
                    key="approve"
                    type="default"
                    icon={<CheckOutlined />}
                    style={isBlocked ? {} : { backgroundColor: statusApprovedActionColor, color: 'white' }}
                    disabled={isBlocked}
                    onClick={() => handleApproveAndNext(currentProductIndex)}
                >
                    {/* // handleApproveAndNext(currentProductIndex)}> */}
                    {isBlocked ? "Blocked" : currentProductIndex === shopifyMedia.length - 1 ? "Approve" : "Approve & Next"}
                </Button>,
                <Button key="close" type="default" icon={<CloseOutlined />} onClick={handleClose}>
                    Close
                </Button>,
            ]}
            styles={{ body: { height: "75vh", overflow: "hidden" } }}
        >
            <div style={{
                maxHeight: "72vh", // Ensure it doesn't exceed modal height
                overflowY: "auto", // Enable vertical scrolling if content exceeds height
                padding: "16px", // Add padding for better readability
            }}>
                <ShopifyProductImageDetailDescription shopifyMedia={selectedShopifyMedia} handleDiscard={handleDiscard} />
            </div>
        </Modal>
    );
};

export default ShopifyProductImageDetailModal;
